import React, { useState, useRef, useEffect } from 'react';
import { Clock, X } from 'lucide-react';

interface SnoozePickerProps {
  isSnoozed?: boolean;
  onSnooze: (snoozeUntil: string | null) => void;
}

function getSnoozePresets(): Array<{ label: string; value: string }> {
  const now = new Date();

  const oneHour = new Date(now.getTime() + 60 * 60 * 1000);

  const fourHours = new Date(now.getTime() + 4 * 60 * 60 * 1000);

  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(9, 0, 0, 0);

  const nextMonday = new Date(now);
  const dayOfWeek = nextMonday.getDay();
  const daysUntilMonday = dayOfWeek === 0 ? 1 : 8 - dayOfWeek;
  nextMonday.setDate(nextMonday.getDate() + daysUntilMonday);
  nextMonday.setHours(9, 0, 0, 0);

  return [
    { label: '1 hour', value: oneHour.toISOString() },
    { label: '4 hours', value: fourHours.toISOString() },
    { label: 'Tomorrow 9 AM', value: tomorrow.toISOString() },
    { label: 'Next Monday 9 AM', value: nextMonday.toISOString() },
  ];
}

export function SnoozePicker({ isSnoozed, onSnooze }: SnoozePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const presets = getSnoozePresets();

  function handleSelect(value: string | null) {
    onSnooze(value);
    setIsOpen(false);
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
          isSnoozed
            ? 'bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100'
            : 'bg-gray-50 text-gray-700 border border-gray-200 hover:bg-gray-100'
        }`}
      >
        <Clock size={14} />
        {isSnoozed ? 'Snoozed' : 'Snooze'}
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
          <div className="p-1">
            {isSnoozed && (
              <button
                onClick={() => handleSelect(null)}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-md transition-colors"
              >
                <X size={14} />
                Unsnooze
              </button>
            )}
            {presets.map((preset) => (
              <button
                key={preset.label}
                onClick={() => handleSelect(preset.value)}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md transition-colors"
              >
                <Clock size={14} className="text-gray-400" />
                {preset.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
